import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const CategorySubcategoryFilter = ({ filterCategoryList, filterSelectedData, onSelectionChange }) => {
    const { t } = useTranslation();
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showExtra, setShowExtra] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowExtra(false);
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (filterSelectedData) {
            const categoryIds = filterSelectedData?.categoryIdArray
                ? String(filterSelectedData.categoryIdArray).split(',').map(Number)
                : [];

            const subcategoryIds = filterSelectedData?.subCategoryIdArray
                ? String(filterSelectedData.subCategoryIdArray).split(',').map(Number)
                : [];

            setSelectedCategories(categoryIds);
            setSelectedSubcategories(subcategoryIds);
        }
    }, [filterSelectedData]);

    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
    const toggleExtra = (e) => {
        e.stopPropagation();
        setShowExtra(!showExtra);
    };

    const notifyFilterChange = (catIds, subcatIds) => {
        const categoryNames = filterCategoryList
            .filter(c => catIds.includes(c.categoryid))
            .map(c => c.categoryname);

        const filterData = {
            categories: catIds,
            subcategories: subcatIds,
            categoriesOnly: categoryNames,
        };

        if (onSelectionChange) {
            onSelectionChange(filterData);
        }
    };

    const handleCategoryChange = (categoryId) => {
        let updated;
        if (selectedCategories.includes(categoryId)) {
            updated = selectedCategories.filter(id => id !== categoryId);
        } else {
            updated = [...selectedCategories, categoryId];
        }
        setSelectedCategories(updated);
        notifyFilterChange(updated, selectedSubcategories);
    };

    const handleSubcategoryChange = (subcategoryId) => {
        let updated;
        if (selectedSubcategories.includes(subcategoryId)) {
            updated = selectedSubcategories.filter(id => id !== subcategoryId);
        } else {
            updated = [...selectedSubcategories, subcategoryId];
        }
        setSelectedSubcategories(updated);
        notifyFilterChange(selectedCategories, updated);
    };

    const removeSelectedItem = (item) => {
        if (item.type === "category") {
            const updated = selectedCategories.filter(id => id !== item.id);
            setSelectedCategories(updated);
            notifyFilterChange(updated, selectedSubcategories);
        } else {
            const updated = selectedSubcategories.filter(id => id !== item.id);
            setSelectedSubcategories(updated);
            notifyFilterChange(selectedCategories, updated);
        }
    };

    const previewItems = useMemo(() => {
        const items = [];
        filterCategoryList.forEach((category) => {
            if (selectedCategories.includes(category.categoryid)) {
                items.push({
                    key: `cat-${category.categoryid}`,
                    id: category.categoryid,
                    name: category.categoryname,
                    type: "category"
                });
            }
            category.subcategories.forEach((subcat) => {
                if (selectedSubcategories.includes(subcat.subcategoryid)) {
                    items.push({
                        key: `sub-${subcat.subcategoryid}`,
                        id: subcat.subcategoryid,
                        name: subcat.subcategoryname,
                        type: "subcategory"
                    });
                }
            });
        });
        return items;
    }, [selectedCategories, selectedSubcategories, filterCategoryList]);

    const inlineItems = previewItems.slice(0, 2);
    const extraItems = previewItems.slice(2);

    const getChipClasses = (type) => {
        return type === "category" ? "bg-blue-light text-blue-normal" : "bg-yellow-light text-yellow-normal";
    };

    const filteredCategories = useMemo(() => {
        if (!searchTerm.trim()) return filterCategoryList;
        return filterCategoryList
            .map((category) => {
                const categoryMatch = category.categoryname.toLowerCase().includes(searchTerm.toLowerCase());
                const filteredSubcats = category.subcategories.filter((subcat) =>
                    subcat.subcategoryname.toLowerCase().includes(searchTerm.toLowerCase())
                );
                if (categoryMatch || filteredSubcats.length > 0) {
                    return { ...category, subcategories: filteredSubcats };
                }
                return null;
            })
            .filter(Boolean);
    }, [searchTerm, filterCategoryList]);

    return (
        <div ref={dropdownRef} className="relative inline-block w-full sm:w-96">
            <div onClick={toggleDropdown} className="p-2 border border-gray-300 rounded cursor-pointer bg-white">
                {previewItems.length === 0 ? (
                    <span className="text-gray-600 capitalize">{ t('filter by category/subcategory')}</span>
                ) : (
                    <div className="flex justify-between items-center">
                        <div className="flex flex-wrap gap-1 text-xs sm:text-sm">
                            {inlineItems.map((item) => (
                                <div key={item.key} className={`inline-flex items-center px-2 py-1 rounded ${getChipClasses(item.type)}`}>
                                    <span>{item.name.length > 10 ? `${item.name.substring(0, 11)}...` : item.name}</span>
                                    <button onClick={(e) => { e.stopPropagation(); removeSelectedItem(item); }} className="ml-2 text-sm font-bold">&times;</button>
                                </div>
                            ))}
                        </div>
                        {extraItems.length > 0 && (
                            <div className="relative">
                                <button onClick={toggleExtra} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    <svg className={`w-4 h-4 transform transition-transform ${showExtra ? "rotate-180" : ""}`}
                                        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7 7 7M5 14l7 7 7-7"/>
                                    </svg>
                                </button>
                                {showExtra && (
                                    <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-300 rounded shadow-lg z-50 p-2">
                                        <div className="text-sm font-semibold mb-1 text-gray-600 capitalize">{ t('selected')}</div>
                                        <div className="max-h-40 overflow-y-auto">
                                            {extraItems.map((item) => (
                                                <div key={item.key} className={`flex items-center justify-between px-2 py-1 mb-1 rounded ${getChipClasses(item.type)}`}>
                                                    <span className="text-sm">{item.name}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); removeSelectedItem(item); }} className="font-bold">&times;</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {dropdownOpen && (
                <div className="absolute space-y-2 w-72 sm:w-96 bg-white border border-gray-300 rounded shadow-lg z-40 p-3" onClick={(e) => e.stopPropagation()}>
                    {/* Search Box */}
                    <div className="relative mb-3">
                        <input type="text" placeholder={t('search')+"..."} className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded focus:outline-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
                        <svg className="w-4 h-4 text-gray-500 absolute right-3 top-3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M9.5 17A7.5 7.5 0 109.5 2a7.5 7.5 0 000 15z"/>
                        </svg>
                    </div>
                    <div className="max-h-44 overflow-y-auto overflow-visible">
                        {filteredCategories.map((category) => (
                            <div key={category.categoryid} className="space-y-2.5">
                                <div className="flex items-center">
                                    <label className="relative flex items-center cursor-pointer">
                                        <input type="checkbox" checked={selectedCategories.includes(category.categoryid)} onChange={() => handleCategoryChange(category.categoryid)} className={`before:content[''] peer relative h-6 w-6  appearance-none rounded border border-gray-dark transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-6 before:w-6 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-white before:opacity-0 before:transition-opacity checked:border-blue-normal checked:bg-blue-normal checked:before:bg-blue-normal cursor-pointer`}/>
                                        <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" strokeWidth="1">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                            </svg>
                                        </span>
                                    </label>
                                    <div className="font-normal text-xs sm:text-base ml-2 text-light-400">{category.categoryname}</div>
                                </div>
                                <div className="ml-6  space-y-1.5">
                                    {category.subcategories.map((subcat) => (
                                        <div key={subcat.subcategoryid} className={`flex items-center `}>
                                            <label className="relative flex items-center cursor-pointer">
                                                <input type="checkbox" checked={selectedSubcategories.includes(subcat.subcategoryid)} onChange={() => handleSubcategoryChange(subcat.subcategoryid)} className={`before:content[''] peer relative h-6 w-6  appearance-none rounded border border-gray-dark transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-6 before:w-6 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-white before:opacity-0 before:transition-opacity checked:border-blue-normal checked:bg-blue-normal checked:before:bg-blue-normal cursor-pointer`}/>
                                                <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" strokeWidth="1">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                                    </svg>
                                                </span>
                                            </label>
                                            <div className="font-normal text-xs sm:text-base ml-2 text-light-400">{subcat.subcategoryname}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategorySubcategoryFilter;
